import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { Shield, Building, Users, CreditCard, ScrollText, LayoutDashboard, ArrowLeft, Mail, Gauge, Inbox } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { BrandMark } from '@/components/brand/BrandMark';

const NAV = [
  { to: '/platform-admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/platform-admin/tenants', label: 'Tenants', icon: Building },
  { to: '/platform-admin/users', label: 'Users', icon: Users },
  { to: '/platform-admin/subscriptions', label: 'Subscriptions', icon: CreditCard },
  { to: '/platform-admin/cost', label: 'LLM cost', icon: Gauge },
  { to: '/platform-admin/leads', label: 'Marketing leads', icon: Mail },
  { to: '/platform-admin/snapshot-intakes', label: 'Snapshot intakes', icon: Inbox },
  { to: '/platform-admin/audit', label: 'Cross-tenant audit', icon: ScrollText },
];

export function PlatformAdminLayout() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  if (!user) return <Navigate to="/login" replace />;
  if (!user.isSuperAdmin) return <Navigate to="/" replace />;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      {/* Sidebar */}
      <aside className="flex w-[240px] shrink-0 flex-col border-r bg-card">
        <div className="flex h-16 items-center gap-2 border-b px-5">
          <BrandMark variant="icon" size="sm" />
          <div>
            <div className="font-display text-sm font-semibold">Praxara</div>
            <div className="text-[10px] uppercase tracking-wider text-primary">Platform admin</div>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t p-3">
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to app
          </Button>
          <div className="mt-3 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-[10px] text-muted-foreground">
            <div className="flex items-center gap-1 font-medium text-primary">
              <Shield className="h-3 w-3" /> Superadmin mode
            </div>
            <div className="mt-0.5">{user.email}</div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
